import React from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Alert, Container, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import useRecentlyViewedProduct from '~/hooks/useRecentlyViewedProduct';

import { updateFlightCampaign } from '~/services/FlightsService';

import { FLIGHT_CAMPAIGN_QUERY_KEY, useFlightCampaign } from '../hooks/useFlightCampaign';
import { FLIGHT_CAMPAIGN_DEALS_QUERY_KEY, useFlightCampaignDeals } from '../hooks/useFlightCampaignDeals';

import { FlightsCampaignsDealsTable } from './FlightsCampaignsDealsTable';
import { FlightsCampaignsForm, OnSaveFormData } from './FlightsCampaignsForm';

function getFlightCampaignRecentlyViewedLabel(campaign?: App.FlightCampaign) {
  const title = campaign?.title?.trim();
  const header = campaign?.header?.trim();

  if (title && header && title !== header) {
    return `${title} - ${header}`;
  }

  return title || header;
}

export default function FlightsCampaignsEdit() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data, error, loading } = useFlightCampaign(id);
  const { enqueueSnackbar } = useSnackbar();
  const { data: deals, loading: loadingCampaignDeals, error: errorCampaignDeals } = useFlightCampaignDeals(id);

  useRecentlyViewedProduct({
    id: id ? `flights:campaign:${id}` : undefined,
    label: getFlightCampaignRecentlyViewedLabel(data),
    productLabel: 'Flights',
  });

  const updateMutation = useMutation({
    mutationFn: (formData: OnSaveFormData) =>
      updateFlightCampaign(id, {
        brand: formData.brand.join(','),
        type: formData.type,
        title: formData.title,
        region: formData.region,
        header: formData.header,
        description: formData.description,
        start_date: formData.startDate,
        end_date: formData.endDate,
        hero_image_public_id: formData.heroImage,
        terms: formData.terms,
        airline_code: formData.airlineCode,
        price: parseFloat(formData.price),
        fare_class: formData.fareClass,
        fare_type: formData.fareType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGN_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [FLIGHT_CAMPAIGN_DEALS_QUERY_KEY, id] });
      enqueueSnackbar('Campaign was updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
    },
  });

  const handleUpdate = (formData: OnSaveFormData) => {
    updateMutation.mutate(formData);
  };

  const showForm = !error && !loading && data;

  return (
    <Container maxWidth="xl">
      <PageHeader title="Edit Campaign" backButton />

      <Stack gap={6}>
        {error && <Alert severity="error">Something went wrong, please try again</Alert>}

        {showForm && (
          <FlightsCampaignsForm
            onSubmit={handleUpdate}
            loading={updateMutation.isPending}
            error={updateMutation.isError ? 'Something went wrong, please try again' : undefined}
            initialValues={{
              brand: data.brand.split(','),
              description: data.description,
              endDate: data.end_date,
              startDate: data.start_date,
              header: data.header,
              heroImage: data.hero_image_public_id,
              region: data.region,
              title: data.title,
              terms: data.terms,
              type: data.type,
              airlineCode: data.airline_code,
              price: data.price.toString(),
              fareClass: data.fare_class,
              fareType: data.fare_type,
            }}
          />
        )}

        <Stack gap={2}>
          <Typography variant="h5" fontWeight={600}>
            Camapaign linked Deals
          </Typography>

          <FlightsCampaignsDealsTable data={deals} error={errorCampaignDeals?.message} loading={loadingCampaignDeals} />
        </Stack>
      </Stack>
    </Container>
  );
}
